import { MenuItem } from 'src/app/layout/models/layout-models';
import { filterMenuByScope } from 'src/app/auth/services/role-scope';
import { canUseOfflineCatalog, isOfflineAllowedUrl } from 'src/app/core/offline/offline-routes';

export type MenuProfile = 'desktop' | 'tablet' | 'mobile';
export const MENU_PROFILES: MenuProfile[] = ['desktop', 'tablet', 'mobile'];

export interface MenuLayout {
  order: string[];
  childOrder: { [parentId: string]: string[] };
  hidden: string[];
}

export type MenuLayoutPref = { [P in MenuProfile]?: MenuLayout };

export const MENU_LAYOUT_KEY = 'display.menuLayout';

export const EMPTY_LAYOUT: MenuLayout = { order: [], childOrder: {}, hidden: [] };

/** A menu item carrying its resolved visibility. `hidden` is the item's OWN stored flag;
 *  `hiddenByParent` means the item is inside a hidden section, so it does not render even though
 *  its own flag is off. Only the editor (`includeHidden`) ever sees `hiddenByParent` — the sidenav
 *  drops a hidden section outright, children included. */
export type ResolvedMenuItem = Omit<MenuItem, 'children'> & {
  children?: ResolvedMenuItem[];
  hidden?: boolean;
  hiddenByParent?: boolean;
};

/** Order `items` by the ids listed in `order` (known ids first, in that order), then append
 *  any id not listed in its original env position. Unknown/duplicate ids in `order` are ignored. */
function applyOrder<T extends { id?: string }>(items: T[], order: string[]): T[] {
  const byId = new Map(items.map(i => [i.id, i] as const));
  const seen = new Set<string>();
  const ordered: T[] = [];
  for (const id of order) {
    const item = byId.get(id);
    if (item && !seen.has(id)) {
      ordered.push(item);
      seen.add(id);
    }
  }
  for (const item of items) {
    if (!item.id || !seen.has(item.id)) ordered.push(item);
  }
  return ordered;
}

/** Role gate — extracted verbatim from the sidenav constructor so the sidenav AND the Settings
 *  editor share one implementation. A module whose own `roles` do not intersect the user's is
 *  dropped; then each child is kept iff its roles intersect (or it has none), dropping the module
 *  when nothing survives. A parent's roles admit the module, NOT every child: a child narrower than
 *  its parent (Marketplaces > Order status report) is gated on its own roles too -- the first
 *  version kept a parent-with-roles module whole and showed that link to a plain user. Never
 *  mutates the input.
 *
 *  A confined role (ROLE_SCOPES in role-scope.ts) is then cut down to the routes its scope allows,
 *  so an entry with no `roles` (visible to everyone) cannot leak into a confined role's menu and the
 *  sidenav never offers a link RoleScopeGuard would bounce. */
export function filterMenuByRoles(menu: MenuItem[], roles: string[]): MenuItem[] {
  const roleSet = new Set(roles);
  const allowed = (i: MenuItem) => !i.roles || i.roles.length === 0 || i.roles.some(r => roleSet.has(r));
  const out: MenuItem[] = [];
  for (const item of menu) {
    if (!allowed(item)) continue;
    if (item.children && item.children.some(c => c.roles && c.roles.length > 0)) {
      const kept = item.children.filter(allowed);
      if (kept.length > 0) out.push({ ...item, children: kept });
      continue;
    }
    out.push({ ...item, children: item.children ? [...item.children] : item.children });
  }
  return filterMenuByScope(out, roles);
}

/** The sidenav of a tab in OFFLINE MODE (no session, the remembered identity's roles): the role gate
 *  above, then only the entries whose route is inside the offline route trees (`isOfflineAllowedUrl`:
 *  Intelligence > Printings, Sealed and Scan), a section kept only when one of its children survives.
 *  Anything else would be a link `CheckAuthService` bounces back to the offline home (seen 13/10/2026:
 *  an offline iPhone listed "Magazzino", whose Stock entry carries no `roles` and so passed an empty
 *  role list). Roles that may not open the offline catalogue get nothing. Never mutates the input. */
export function filterMenuForOffline(menu: MenuItem[], roles: readonly string[] | null | undefined): MenuItem[] {
  if (!canUseOfflineCatalog(roles)) {
    return [];
  }
  const offline = (item: MenuItem) => !!item.route && isOfflineAllowedUrl(item.route);
  const out: MenuItem[] = [];
  for (const item of filterMenuByRoles(menu, [...(roles ?? [])])) {
    if (item.children && item.children.length > 0) {
      const kept = item.children.filter(offline);
      if (kept.length > 0) {
        // The section header navigates too (`go(parent)`): point it inside the offline trees.
        out.push({ ...item, route: offline(item) ? item.route : kept[0].route, children: kept });
      }
      continue;
    }
    if (offline(item)) {
      out.push({ ...item });
    }
  }
  return out;
}

/** Apply a profile's order + hidden to an ALREADY role-filtered menu. In view mode hidden ids are
 *  dropped; with `includeHidden` they stay, each carrying `hidden: true` for the editor to grey.
 *  A role-forbidden item is already absent from `roleFiltered`, so order/hidden can never add it back.
 *
 *  Hiding a section hides its whole branch: in view mode the section is skipped outright (children
 *  never reach the sidenav), and in editor mode each child is stamped `hiddenByParent` so the editor
 *  can show the inherited state. A child's OWN `hidden` flag is never rewritten, so un-hiding the
 *  section restores exactly the per-child choices the user had made. */
export function resolveLayout(
  roleFiltered: MenuItem[],
  layout: MenuLayout,
  opts: { includeHidden?: boolean } = {},
): ResolvedMenuItem[] {
  const hidden = new Set(layout.hidden);
  const modules = applyOrder(roleFiltered, layout.order);
  const out: ResolvedMenuItem[] = [];
  for (const mod of modules) {
    const modHidden = !!mod.id && hidden.has(mod.id);
    if (modHidden && !opts.includeHidden) continue;
    let children: ResolvedMenuItem[] | undefined = mod.children;
    if (mod.children && mod.id) {
      children = applyOrder(mod.children, layout.childOrder[mod.id] ?? [])
        .filter(c => opts.includeHidden || !(c.id && hidden.has(c.id)))
        .map(c => ({ ...c, hidden: !!c.id && hidden.has(c.id), hiddenByParent: modHidden }));
    }
    out.push({ ...mod, children, hidden: modHidden });
  }
  return out;
}

export function layoutFor(pref: MenuLayoutPref | undefined, profile: MenuProfile): MenuLayout {
  return pref?.[profile] ?? EMPTY_LAYOUT;
}

const asStringArray = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];

function coerceLayout(v: unknown): MenuLayout {
  const o = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
  const childOrderRaw = (o['childOrder'] && typeof o['childOrder'] === 'object' ? o['childOrder'] : {}) as Record<string, unknown>;
  const childOrder: { [k: string]: string[] } = {};
  // Skip a payload key literally named '__proto__': assigning it via bracket notation would invoke
  // the inherited prototype setter and rebind this object's [[Prototype]] instead of adding an own
  // property. Matches the prototype-safe posture of the shared coercePref util.
  for (const k of Object.keys(childOrderRaw)) {
    if (k === '__proto__') continue;
    childOrder[k] = asStringArray(childOrderRaw[k]);
  }
  return { order: asStringArray(o['order']), childOrder, hidden: asStringArray(o['hidden']) };
}

/** Coerce an untrusted blob/broadcast payload to a MenuLayoutPref: object → per-profile coerced
 *  Layout, anything else → {}. Only the three known profile keys are read. */
export function coerceMenuLayout(v: unknown): MenuLayoutPref {
  if (!v || typeof v !== 'object') return {};
  const src = v as Record<string, unknown>;
  const out: MenuLayoutPref = {};
  for (const p of MENU_PROFILES) {
    if (src[p] && typeof src[p] === 'object') out[p] = coerceLayout(src[p]);
  }
  return out;
}
